/**
 * Діалог «Запросити користувача» (Порція 3.0.5.4 — повна форма).
 *
 * Поля:
 *   - email — кому надсилаємо.
 *   - tenant_role — MEMBER (з pre-grant user_access) або ADMIN
 *     (повний доступ до tenant'а, pre-grant ігнорується backend'ом).
 *   - initial_user_access[] (тільки для MEMBER) — для кожної org tenant'а
 *     можна вибрати access_role (PROJECT_ADMIN / CHIEF_ACCOUNTANT /
 *     FINANCIAL_ANALYST / AUDITOR / OBSERVER / VIEWER / ...) АБО
 *     «Без доступу» (не створюємо запис).
 *
 * Після створення (ч.1073) — запрошення надсилається листом (і в Telegram,
 * якщо в існуючого користувача підключено); посилання для копіювання
 * лишається як запасний шлях (InvitationResult).
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  createInvitation,
  type InvitationCreated,
  type TenantRole,
} from '@/api/invitations';
import { orgs } from '@/api/catalogs';
import { accessRoles } from '@/api/admin';
import { getMe } from '@/api/auth';
import InvitationResult from './InvitationResult';
import OrgAccessTable, { toGrants } from './OrgAccessTable';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export default function InviteUserDialog({ onClose, onCreated }: Props) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('MEMBER');
  /** organization_id → access_role_id або '' (= без доступу). */
  const [access, setAccess] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<InvitationCreated | null>(null);
  // ч.1074: безпека нового користувача — лише власник акаунта.
  const [req2fa, setReq2fa] = useState(false);
  const [ipOnly, setIpOnly] = useState(false);
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const isOwner = meQ.data?.tenant_role === 'OWNER';
  const [localError, setLocalError] = useState<string | null>(null);

  // Завантажуємо доступні організації tenant'а.
  const orgsQuery = useQuery({
    queryKey: ['orgs-list'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  // Завантажуємо доступні access roles.
  const rolesQuery = useQuery({
    queryKey: ['access-roles'],
    queryFn: () => accessRoles.list(),
  });

  const mut = useMutation({
    mutationFn: () => {
      const grants = role === 'MEMBER' ? toGrants(access) : [];
      return createInvitation({
        email: email.trim(),
        tenant_role: role,
        initial_user_access: grants,
        ...(isOwner ? { require_two_factor: req2fa, ip_restricted: ipOnly } : {}),
      });
    },
    onSuccess: (data) => setCreated(data),
    onError: (e) => {
      const err = e as AxiosError<{ detail?: string }>;
      setLocalError(err.response?.data?.detail ?? 'Не вдалося створити запрошення');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (!email.trim()) {
      setLocalError('Введіть email');
      return;
    }
    mut.mutate();
  }

  function handleClose() {
    if (created) onCreated();
    else onClose();
  }

  const orgsList = orgsQuery.data?.items ?? [];
  const rolesList = rolesQuery.data ?? [];

  return (
    <>
      <div className="backdrop" onClick={handleClose} />
      <div className="drawer" style={{ width: 'min(640px, 100vw)' }}>
        <h2>Запросити користувача</h2>

        {!created && (
          <form onSubmit={onSubmit}>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                autoComplete="off"
                required
                autoFocus
              />
            </div>
            <div className="field">
              <label>Роль в акаунті</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as TenantRole)}
              >
                <option value="MEMBER">Користувач — доступи налаштовуються нижче</option>
                <option value="ADMIN">Адміністратор — повний доступ до всіх юросіб</option>
              </select>
              <small style={{ color: 'var(--text-secondary)' }}>
                «Власник акаунта» через запрошення не призначається — ним є
                той, хто зареєстрував акаунт.
              </small>
            </div>

            {role === 'MEMBER' && (
              <div className="field">
                <label>Доступи до юросіб (опційно)</label>
                {orgsQuery.isLoading && <div>Завантаження…</div>}
                {!orgsQuery.isLoading && orgsList.length === 0 && (
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    У вас ще немає юросіб. Створіть їх у /organizations,
                    після цього можна буде вибирати доступ.
                  </div>
                )}
                {orgsList.length > 0 && (
                  <OrgAccessTable orgs={orgsList} roles={rolesList}
                    value={access} onChange={setAccess} />
                )}
                <small style={{ color: 'var(--text-secondary)' }}>
                  Можна не вибирати зараз — доступи змінюються будь-коли:
                  кнопка «доступи» біля користувача.
                </small>
              </div>
            )}

            {isOwner && (
              <div className="field">
                <label>Безпека</label>
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 400 }}>
                  <input type="checkbox" style={{ width: 'auto' }} checked={req2fa}
                    onChange={(e) => setReq2fa(e.target.checked)} />
                  вимагати двофакторний вхід
                </label>
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 400 }}>
                  <input type="checkbox" style={{ width: 'auto' }} checked={ipOnly}
                    onChange={(e) => setIpOnly(e.target.checked)} />
                  працювати лише з довірених мереж (IP)
                </label>
                <small style={{ color: 'var(--text-secondary)' }}>
                  Мережі та інші налаштування — вкладка «Безпека».
                </small>
              </div>
            )}

            {localError && <div className="error">{localError}</div>}

            <div className="drawer__actions">
              <button type="button" onClick={onClose}>
                скасувати
              </button>
              <button
                type="submit"
                className="primary"
                disabled={mut.isPending}
              >
                {mut.isPending ? 'надсилання…' : 'надіслати запрошення'}
              </button>
            </div>
          </form>
        )}

        {created && (
          <div>
            <InvitationResult created={created} />
            <div className="drawer__actions">
              <button type="button" className="primary" onClick={handleClose}>
                готово
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
